import * as React from "react";

import { faHdd, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { Apps, WatchLater, Summarize, Warning } from "@mui/icons-material";
import { Tooltip, tooltipClasses, TooltipProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import * as Sentry from "@sentry/nextjs";
import { DateTime } from "luxon";
import TimeAgo from "react-timeago";

import { FontAwesomeIconWithTheme } from "src/common/components/ui/IconWrapper";
import { schedulerDisplayName } from "src/common/textAlteration";
import { WorkflowControlButtons } from "src/domain/core/workflow/components/active/HeaderWorkflowButtons";
import { AWSStatusDisplay, getWorkflowMainIcon } from "src/domain/core/workflow/components/active/Status";
import * as Tags from "src/lib/tags";

const useStyles = makeStyles(() => ({
  flash: {
    animation: `$flashing 2.5s linear infinite`,
  },
  "@keyframes flashing": {
    "50%": {
      opacity: 0.3,
    },
  },
}));

const MoreInfoLink = styled("span")(() => ({}));
const Info = styled("div")(({ theme }) => ({
  flexDirection: "column",
  width: "100%",
  wordBreak: "break-all",

  svg: {
    fontSize: "1em",
    margin: "0px 5px 3px 5px",
    color: theme.palette.primary.main,
    verticalAlign: "middle",
  },

  [`& ${MoreInfoLink}:hover`]: {
    cursor: "pointer",
    textDecoration: "underline",
  },
}));

const WarningLine = styled("div")(({ theme }) => ({
  svg: {
    fontSize: "1em",
    margin: "0px 5px 3px 5px",
    color: theme.palette.warning.main,
    verticalAlign: "middle",
  },
}));

// https://mui.com/material-ui/react-tooltip/#VariableWidth.tsx
const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
  },
});

function isSchedulerDeprecated(schedulerDeprecationDate: DateTime): boolean {
  if (schedulerDeprecationDate) {
    return schedulerDeprecationDate <= DateTime.now();
  }

  return false;
}

function getSchedulerWarningTooltipMessage(scheduler: Scheduler, schedulerDeprecationDate: DateTime): string {
  return `This workflow is using the job scheduler "${schedulerDisplayName(
    scheduler?.name
  )}", which is deprecated as of ${schedulerDeprecationDate?.toLocaleString(
    DateTime.DATE_MED
  )}. We recommend using a different job-scheduler. Please contact your organization administrator for guidance.`;
}

interface HeaderProps {
  workflow: {
    id: string;
    name: string;
    created_at: string;
    status: string;
    tags?: Record<string, string>;
  };
  disk: {
    name: string;
  };
  scheduler: Scheduler;
  deleteDisabled: boolean;
  updateDisabled: boolean;
  infoClick: () => void;
  updateClick: () => Promise<void>;
  deleteClick: () => Promise<void>;
  syncKey: string;
}

export const Header: React.FC<HeaderProps> = ({
  workflow,
  disk,
  scheduler,
  deleteDisabled,
  updateDisabled,
  infoClick,
  updateClick,
  deleteClick,
  syncKey,
}) => {
  const classes = useStyles();

  const schedulerDeprecationDate = React.useMemo(() => {
    return DateTime.fromISO(scheduler?.deprecation_date);
  }, [scheduler]);

  React.useEffect(() => {
    if (isNaN(schedulerDeprecationDate)) {
      Sentry.captureException(
        `could not parse deprecation date into Date object for scheduler "${scheduler?.name}" with deprecation date "${scheduler?.deprecation_date}"`
      );
    }
  }, [scheduler, schedulerDeprecationDate]);

  const workflowName = workflow.tags?.[Tags.workflowDisplayName] || workflow.name;

  return (
    <>
      {getWorkflowMainIcon(workflow?.status, classes, syncKey)}
      <Info>
        <Typography variant="h6" color="textPrimary">
          <MoreInfoLink onClick={infoClick} data-testid={`${workflowName}-info`}>
            <span>{workflowName}</span>
            <FontAwesomeIconWithTheme icon={faInfoCircle} title="info" />
          </MoreInfoLink>
        </Typography>
        <Typography variant="body1" color="textPrimary">
          <WatchLater />
          Created: <TimeAgo minPeriod={15} date={new Date(workflow.created_at)} />
        </Typography>
        {workflow?.tags?.[Tags.templateName] && (
          <Typography variant="body1" color="textPrimary">
            <FontAwesomeIconWithTheme icon={faHdd} style={{ width: "1em" }} />
            Template: {workflow.tags[Tags.templateName]}
          </Typography>
        )}
        <Typography variant="body1" color="textPrimary">
          <Apps />
          Blueprint: {workflow?.tags?.[Tags.blueprintName]}
        </Typography>
        <Typography variant="body1" color="textPrimary">
          <FontAwesomeIconWithTheme icon={faHdd} style={{ width: "1em" }} />
          Disk: {disk?.name}
        </Typography>
        <Typography variant="body1" color="textPrimary">
          <Summarize />
          Status: {AWSStatusDisplay[workflow.status] || "Status Unknown"}
        </Typography>
        {isSchedulerDeprecated(schedulerDeprecationDate) && (
          <WarningLine>
            <CustomWidthTooltip
              sx={{ maxWidth: 500 }}
              title={getSchedulerWarningTooltipMessage(scheduler, schedulerDeprecationDate)}
            >
              <Typography variant="body1">
                <Warning />
                {/* TODO: Link to KB article */}
                Warning: This workflow is using an <b>outdated scheduler</b>.
              </Typography>
            </CustomWidthTooltip>
          </WarningLine>
        )}
      </Info>
      <WorkflowControlButtons
        workflowName={workflowName}
        status={workflow?.status}
        diskName={disk?.name}
        deleteDisabled={deleteDisabled}
        updateDisabled={updateDisabled}
        updateClick={updateClick}
        deleteClick={deleteClick}
      />
    </>
  );
};
